import { useState } from "react";
import { motion } from "framer-motion";

export default function TermsAndConditions() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: "introduction",
      title: "Introduction & Agreement",
      content: `These Terms and Conditions (the "Terms") constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Techspear Ventures LLC, a Delaware limited liability company headquartered in Atlanta, Georgia ("Techspear Ventures," "we", "us", or "our") and govern your access to and use of the www.techspearventures.com website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site").`
    },
    {
      id: "definitions",
      title: "Certain Definitions",
      content: `• "Content" means source code, images, photos, databases, functionality, software, website designs, audio, video, text, photographs, and graphics and all other forms of data or communication.
• "Your Content" means Content that you submit or transmit to, through, or in connection with the Site.
• "User Content" means Content that users submit or transmit to, through, or in connection with the Site.
• "Techspear Ventures Content" means Content that we create and make available in connection with the Site.
• "Third Party Content" means Content that originates from parties other than Techspear Ventures or its users.
• "Site Content" means all of the Content that is made available in connection with the Site.`
    },
    {
      id: "changes",
      title: "Changes to Terms of Services",
      content: `We may modify the Terms from time to time. You understand and agree that your access to or use of the Site is governed by the Terms effective at the time of your access to or use of the Site. If we make material changes to these Terms, we will notify you by email or by posting a revised version of the Terms on the Site.`
    },
    {
      id: "purpose",
      title: "Purpose of the Site",
      content: `The purpose of the site is to provide general information on our services. We do not offer investment advice through the Site. Nothing on the Site is intended to be, nor should it be construed or used as, financial, legal, tax or investment advice or an offer to sell, or a solicitation of any offer to buy any securities, interest in a fund, or other financial instruments.`
    },
    {
      id: "using-site",
      title: "Using the Site",
      content: `• Eligibility: You must be 18 years or older to use the Site
• Permission to Use: We grant you permission subject to restrictions
• Site Availability: May be modified or discontinued at any time
• User Accounts: You are responsible for maintaining account security
• Communications: By creating an account, you agree to receive communications`
    },
    {
      id: "content",
      title: "Content & Intellectual Property",
      content: `You alone are responsible for Your Content and assume all risks associated with it. Unless otherwise indicated, the Site is our proprietary property and all Content and Marks are owned or controlled by us and are protected by copyright and trademark laws.`
    },
    {
      id: "restrictions",
      title: "Restrictions",
      content: `You agree not to use the Site to violate any laws, harass others, send bulk emails, compete with us, or engage in unauthorized activities. You also agree not to reverse engineer, mine information, or interfere with the Site's functionality.`
    },
    {
      id: "events",
      title: "Events",
      content: `Techspear Ventures Events are organized by us, while Non-Techspear Ventures Events are organized by others but may appear on our Site. You assume all risks when attending events and release Techspear Ventures from liability except for gross negligence.`
    },
    {
      id: "privacy",
      title: "Privacy & Intellectual Property",
      content: `We care about data privacy and security. Please review our Privacy Policy. The Site is hosted in the United States, and by using it, you consent to data transfer to the US.`
    },
    {
      id: "liability",
      title: "Disclaimers & Limitations of Liability",
      content: `THE SITE IS PROVIDED "AS IS" WITHOUT WARRANTIES. TECHSPEAR VENTURES ENTITIES ARE NOT LIABLE FOR ANY LOSSES OR DAMAGES. YOUR SOLE REMEDY IS DISCONTINUING USE OF THE SITE. MAXIMUM LIABILITY IS LIMITED TO $100.`
    },
    {
      id: "governance",
      title: "Governing Law & Dispute Resolution",
      content: `Georgia law governs these Terms. Disputes must first be negotiated informally for 30 days, then resolved through binding arbitration in Fulton County, Georgia.`
    },
    {
      id: "general",
      title: "General Terms",
      content: `We reserve the right to modify or discontinue the Site at any time. These Terms constitute the entire agreement between you and us.`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-16 font-sans tracking-wide">
      <div className="px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
            📄 Legal Documentation
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-mokoto text-gray-900 mb-6">
            Terms and Conditions
          </h1>
          <div className="flex items-center justify-center space-x-4 text-gray-600">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Last Updated: July 10, 2020</span>
            </div>
          </div>
        </motion.div>

        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-12"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                IMPORTANT LEGAL NOTICE
              </h3>
              <p className="text-red-700">
                IF YOU DO NOT AGREE WITH ALL OF THESE TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SITE AND YOU MUST DISCONTINUE USE IMMEDIATELY.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-12"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => {
                  toggleSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-left p-3 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 border border-transparent hover:border-blue-200"
              >
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-xl flex items-center justify-center text-white font-semibold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                </div>
                <svg
                  className={`w-6 h-6 text-gray-500 transform transition-transform duration-200 ${
                    openSections[section.id] ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <motion.div
                initial={false}
                animate={{ 
                  height: openSections[section.id] ? 'auto' : 0,
                  opacity: openSections[section.id] ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                    {section.content.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 text-center text-white mt-16"
        >
          <h2 className="text-3xl font-bold mb-4">Questions About Our Terms?</h2>
          <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
            We're here to help clarify any questions you might have about our Terms and Conditions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:Info@techspearventures.com"
              className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Email Us
            </a>
            <div className="text-blue-100">
              <p className="font-medium">Techspear Ventures</p>
              <p>75 5th Street NW, Suite 427</p>
              <p>Atlanta, Georgia 30308</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}